import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Heart, ShoppingBag, Trash2 } from 'lucide-react';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { useCart } from '../contexts/CartContext';
import { useToast } from '../contexts/ToastContext';
import { formatPrice } from '../lib/format';
import { getProductCardImage } from '../lib/images';

const WishlistPage = () => {
  const { isAuthenticated } = useAuth();
  const { addItem, openDrawer } = useCart();
  const { push: pushToast } = useToast();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!isAuthenticated) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get('/customer/wishlist');
        if (!cancelled && res.data?.success) setItems(res.data.data || []);
      } catch {/* swallow */}
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [isAuthenticated]);

  const remove = async (productId) => {
    try {
      await api.delete(`/customer/wishlist/${productId}`);
      setItems((prev) => prev.filter((p) => p._id !== productId));
    } catch {
      pushToast('Could not remove', { tone: 'error' });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center p-6">
        <div className="bg-white border-4 border-black p-10 max-w-md text-center shadow-[8px_8px_0px_black]">
          <Heart size={48} className="mx-auto mb-4 text-red-500" />
          <h2 className="text-3xl font-black mb-2">LOGIN TO VIEW WISHLIST</h2>
          <p className="text-gray-600 mb-6">Save items you love for later.</p>
          <button
            onClick={() => navigate('/')}
            className="bg-yellow-400 text-black px-6 py-3 font-black border-4 border-black"
          >
            ← Back home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto p-6 md:p-12">
      <h1 className="text-5xl md:text-7xl font-black tracking-tighter mb-8 flex items-center gap-4">
        <Heart size={48} fill="red" className="text-red-500" /> WISHLIST
      </h1>
      {loading ? (
        <div className="text-center text-xl font-bold animate-pulse">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-white border-4 border-black p-16 text-center shadow-[8px_8px_0px_black]">
          <Heart size={64} className="mx-auto mb-4 opacity-40" />
          <h2 className="text-3xl font-black mb-2">YOUR WISHLIST IS EMPTY</h2>
          <Link to="/anime" className="inline-block mt-4 bg-yellow-400 text-black px-6 py-3 font-black border-4 border-black">
            Browse products →
          </Link>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {items.map((p) => {
            const img = getProductCardImage(p);
            return (
              <div key={p._id} className="bg-white border-4 border-black p-4 flex gap-4 shadow-[4px_4px_0px_black]">
                <Link to={`/${p.category}/${p.slug || p._id}`} className="flex-shrink-0">
                  {img ? (
                    <img src={img} alt={p.name} className="w-24 h-24 object-cover border-2 border-black" />
                  ) : (
                    <div className="w-24 h-24 bg-gray-200 border-2 border-black flex items-center justify-center text-4xl font-black">
                      {p.name?.charAt(0)}
                    </div>
                  )}
                </Link>
                <div className="flex-grow min-w-0">
                  <div className="text-xs font-bold uppercase text-gray-500">{p.category}</div>
                  <Link to={`/${p.category}/${p.slug || p._id}`}>
                    <h3 className="font-black text-lg leading-tight truncate hover:underline">{p.name}</h3>
                  </Link>
                  <div className="font-bold mt-1">{formatPrice(p.salePrice ?? p.price)}</div>
                  <div className="flex gap-2 mt-3">
                    <button
                      onClick={() => {
                        addItem(p, { size: p.sizes?.[0]?.size || 'L', quantity: 1 });
                        openDrawer();
                      }}
                      className="flex-grow flex items-center justify-center gap-2 bg-yellow-400 text-black py-2 font-bold text-sm border-2 border-black hover:bg-yellow-300"
                    >
                      <ShoppingBag size={16} /> ADD TO CART
                    </button>
                    <button
                      onClick={() => remove(p._id)}
                      className="p-2 bg-red-100 text-red-600 border-2 border-black hover:bg-red-200"
                      aria-label="Remove from wishlist"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default WishlistPage;
